/**
 * Address Edit Dialog Component
 *
 * Material Dialog for creating/editing addresses
 */

import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CountryService } from '../../core/services/country.service';
import { CategoryDefinitionService } from '../../core/services/category-definition.service';
import type { ContactAddressWithDetails } from '../../core/models';
import { TranslatePipe } from '../../core/features/translations';

export interface AddressEditDialogData {
  address: ContactAddressWithDetails | null;
  contactId: number;
}

export interface AddressEditDialogResult {
  formData: {
    companyname?: string;
    salutation?: string;
    prename?: string;
    lastname?: string;
    street?: string;
    zip?: string;
    town?: string;
    region?: string;
    fk_country_id?: number;
    fk_category_id: number;
  };
}

@Component({
  selector: 'app-address-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    TranslatePipe,
  ],
  template: `
    <div class="p-6 font-sans">
      <h2 class="m-0 mb-6 text-2xl font-semibold text-gray-900 border-b pb-4">
        {{
          (isEditMode()
            ? 'portal.mydata.addresses.dialog.edit'
            : 'portal.mydata.addresses.dialog.add'
          ) | translate
        }}
      </h2>
      
      <div class="max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
        <form [formGroup]="addressForm" class="flex flex-col gap-6">
          <!-- Category Selection - Only for non-primary addresses -->
          @if (!isPrimaryAddress()) {
            <div class="flex flex-col gap-3">
              <h3 class="m-0 text-base font-semibold text-gray-700 uppercase tracking-wider">
                {{ 'portal.mydata.addresses.dialog.type' | translate }}
              </h3>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.category' | translate }} *
                </label>
                <select 
                  formControlName="fk_category_id" 
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                  [class.border-red-500]="addressForm.get('fk_category_id')?.invalid && addressForm.get('fk_category_id')?.touched"
                >
                  <option value="">{{ 'portal.mydata.addresses.dialog.select' | translate }}</option>
                  @for (cat of sideAddressCategories(); track cat.category_id) {
                    <option [value]="cat.category_id">
                      {{ getCategoryTranslationKey(cat.description) | translate }}
                    </option>
                  }
                </select>
                @if (addressForm.get('fk_category_id')?.hasError('required') && addressForm.get('fk_category_id')?.touched) {
                  <span class="text-xs text-red-600 font-medium">
                    {{ 'portal.mydata.addresses.dialog.categoryRequired' | translate }}
                  </span>
                }
              </div>
            </div>
          }

          <!-- Address Details -->
          <div class="flex flex-col gap-4">
            <h3 class="m-0 text-base font-semibold text-gray-700 uppercase tracking-wider">
              {{ 'portal.mydata.addresses.dialog.details' | translate }}
            </h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Company Name -->
              <div class="flex flex-col gap-1.5 md:col-span-2">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.company' | translate }}
                </label>
                <input 
                  type="text" 
                  formControlName="companyname" 
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                />
              </div>

              <!-- Salutation -->
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.salutation' | translate }}
                </label>
                <select 
                  formControlName="salutation"
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                >
                  <option value="">{{ 'portal.mydata.addresses.dialog.select' | translate }}</option>
                  <option value="Herr">{{ 'portal.mydata.addresses.dialog.mr' | translate }}</option>
                  <option value="Frau">{{ 'portal.mydata.addresses.dialog.mrs' | translate }}</option>
                  <option value="Divers">{{ 'portal.mydata.addresses.dialog.diverse' | translate }}</option>
                </select>
              </div>

              <!-- First Name -->
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.firstname' | translate }}
                </label>
                <input 
                  type="text" 
                  formControlName="prename" 
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                />
              </div>

              <!-- Last Name -->
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.lastname' | translate }}
                </label>
                <input 
                  type="text" 
                  formControlName="lastname" 
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                />
              </div>

              <!-- Street -->
              <div class="flex flex-col gap-1.5 md:col-span-2">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.street' | translate }}
                </label>
                <input 
                  type="text" 
                  formControlName="street" 
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                />
              </div>

              <!-- ZIP -->
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.zip' | translate }}
                </label>
                <input 
                  type="text" 
                  formControlName="zip" 
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                />
              </div>

              <!-- City -->
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.city' | translate }}
                </label>
                <input 
                  type="text" 
                  formControlName="town" 
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                />
              </div>

              <!-- Region -->
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.region' | translate }}
                </label>
                <input 
                  type="text" 
                  formControlName="region" 
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                />
              </div>

              <!-- Country -->
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {{ 'portal.mydata.addresses.dialog.country' | translate }}
                </label>
                <select 
                  formControlName="fk_country_id"
                  class="w-full p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                >
                  <option value="">{{ 'portal.mydata.addresses.dialog.select' | translate }}</option>
                  @for (country of countries(); track country.country_id) {
                    <option [value]="country.country_id">
                      {{ country.country_name }}
                    </option>
                  }
                </select>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div class="flex justify-end gap-3 mt-8 pt-6 border-t">
        <button 
          type="button"
          (click)="onCancel()"
          class="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {{ 'portal.mydata.addresses.dialog.cancel' | translate }}
        </button>
        <button
          type="button"
          (click)="onSave()"
          [disabled]="addressForm.invalid || saving()"
          class="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded shadow-sm transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          @if (saving()) {
            <span class="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></span>
            <span>{{ 'portal.mydata.addresses.dialog.saving' | translate }}</span>
          } @else {
            <span>{{ 'portal.mydata.addresses.dialog.save' | translate }}</span>
          }
        </button>
      </div>
    </div>
  `,
  styles: [],
})
export class AddressEditDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AddressEditDialogComponent>);
  private readonly data: AddressEditDialogData = inject(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly countryService = inject(CountryService);
  private readonly categoryService = inject(CategoryDefinitionService);

  readonly saving = signal(false);
  readonly countries = this.countryService.countries;
  readonly sideAddressCategories = this.categoryService.sideAddressCategories;
  readonly isEditMode = signal(this.data.address !== null);
  readonly isPrimaryAddress = signal(this.data.address?.is_primary_address || false);

  addressForm!: FormGroup;

  ngOnInit(): void {
    this.initializeForm();
    this.loadData();
    if (this.data.address) {
      this.patchFormValues(this.data.address);
    }
  }

  private initializeForm(): void {
    // For primary addresses, category is not editable and not required
    const categoryValidators = this.isPrimaryAddress() ? [] : [Validators.required];

    this.addressForm = this.fb.group({
      companyname: [''],
      salutation: [''],
      prename: [''],
      lastname: [''],
      street: [''],
      zip: [''],
      town: [''],
      region: [''],
      fk_country_id: [''],
      fk_category_id: ['', categoryValidators],
    });
  }

  private loadData(): void {
    // Load countries if not already loaded
    if (this.countries().length === 0) {
      this.countryService.getCountries().subscribe();
    }

    // Load side address categories if not already loaded
    if (this.sideAddressCategories().length === 0) {
      this.categoryService.getSideAddressCategories().subscribe();
    }
  }

  private patchFormValues(address: ContactAddressWithDetails): void {
    this.addressForm.patchValue({
      companyname: address.companyname || '',
      salutation: address.salutation || '',
      prename: address.prename || '',
      lastname: address.lastname || '',
      street: address.street || '',
      zip: address.zip || '',
      town: address.town || '',
      region: address.region || '',
      fk_country_id: address.fk_country_id || '',
      fk_category_id: address.fk_category_id || '',
    });
  }

  /**
   * Generate translation key based on category name
   * Converts category description to a translation key format
   */
  getCategoryTranslationKey(categoryDescription: string): string {
    if (!categoryDescription || categoryDescription === '-') {
      return 'portal.mydata.addresses.category.unknown';
    }

    // Convert category name to translation key
    // e.g., "Invoicing" -> "portal.mydata.addresses.category.invoicing"
    const normalizedName = categoryDescription
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/[^a-z0-9_äöüß]/g, ''); // Remove special characters except umlauts

    return `portal.mydata.addresses.category.${normalizedName}`;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    const result: AddressEditDialogResult = {
      formData: this.addressForm.value,
    };

    this.dialogRef.close(result);
  }
}
